import type React from 'react';
import { useState } from 'react';
import Slider from 'react-slick';
import moment from 'moment-timezone';
import classNames from 'classnames';
import { DateCard } from './date-card/DateCard';
import { TIMEZONE } from '../../utils/constants';

import './DatePicker.css';

export interface Props {
  onSelect: (date: Date) => void;
  dates: Date[];
}

const DatePicker: React.FC<Props> = ({ onSelect, dates }) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  const onClick = (date: Date) => () => {
    setSelectedDate(date);
    onSelect(date);
  };

  interface CustomPrevArrowProps {
    className?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    currentSlide?: number;
  }

  const CustomPrevArrow: React.FC<CustomPrevArrowProps> = ({ className, onClick, currentSlide }) => {
    const disabledClass = currentSlide === 0 ? 'prev-arrow-disabled' : '';
    return (
      <button
        className={classNames(className, disabledClass)}
        onClick={onClick}
        disabled={currentSlide === 0} // Disable if on the first slide
        style={{
          cursor: currentSlide === 0 ? "auto" : "pointer",
          opacity: currentSlide === 0 ? 0.5 : 1,
        }}
      >
        Prev
      </button>
    );
  };

  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(5);

  const settings = {
    infinite: false,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 5,
    prevArrow: <CustomPrevArrow />,
    accessibility: true,
    beforeChange: (_oldIndex: number, newIndex: number) => setCurrentSlide(newIndex),
    responsive: [
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 1000,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 4,
        },
      },
    ],
  };

  return (
    <div data-testid="date-picker">
      <Slider {...settings}>
        {dates.map((date, idx) => (
          <div key={moment(date).tz(TIMEZONE).format('X')}>
            <div
              data-testid="slide"
              className={classNames('slide', {
                selected: moment(date).isSame(selectedDate, 'day'),
              })}
              onClick={onClick(date)}
              tabIndex={idx >= currentSlide && idx < currentSlide + slidesToShow ? 0 : -1}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick(date)();
                }
              }}
            >
              <DateCard date={date} />
            </div>
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default DatePicker;